import * as React from 'react';
import { useContext } from 'react';
import { AuthContext } from '../_auth/AuthContext';
import { Button, CircularProgress, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, TextField, Theme, Typography, createStyles, makeStyles, Divider, Tabs, Tab, Box, ButtonGroup } from '@material-ui/core';
import { GetLinkBack, GetDownloadReport, DownloadableReport, GetMetaData, CMSMetaData, CMSMetaDataPost, SaveMetaData } from '../_service/engage';
import { IDownloadFile } from '../_types/post';

interface downloadProps {
    report: string;
    title: string;
    period: string;
    link: string;
}

interface linkBackProps {
    link: string
};

interface surveyProps {
    metaData: CMSMetaData;
    onChange: (metaData: CMSMetaData) => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

const Survey: React.FC<surveyProps> = (prop: surveyProps) => {
    const [ycount, setycount] = React.useState<number>(prop.metaData.yesterdayWashCount);
    const [tcount, settcount] = React.useState<number>(prop.metaData.todayWashCount);
    const [changed, setChanged] = React.useState<boolean>(false);
    const [saving, setSaving] = React.useState<boolean>(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        
        if (value === '' || /^[0-9]+$/.test(value)) {

            let oldTCount = tcount;
            let oldYCount = ycount;

            let newVal:number = value === '' ? 0 : parseInt(value);

            if (name === 'iToday') { 
                
                settcount(newVal);
                oldTCount = newVal;
                
            }
            if (name === 'iYesterday') { 
                setycount(newVal);
                oldYCount = newVal;
                
            }
            setChanged(oldYCount !== prop.metaData.yesterdayWashCount || oldTCount !== prop.metaData.todayWashCount);
            
        }
    };

    const saveChanges = () => {

        setSaving(true);

        let toPost:CMSMetaDataPost = {
            id: prop.metaData.id,
            uuid: prop.metaData.uuid,
            yesterdayWashCount: ycount,
            todayWashCount: tcount
        }
        
        setChanged(false);

        const _doWork = async (data: CMSMetaDataPost): Promise<CMSMetaData | null> => {
            const metaData: CMSMetaData | null = await SaveMetaData(data);
            
            if (metaData) {
                prop.onChange(metaData)
        
            }
            setSaving(false);
            return metaData;
        }

        _doWork(toPost);
        
    }

    return (
        <>
        <div style={{paddingTop: 10}}>
            <Typography variant="h6">Daily Non-Membership Wash Count</Typography><br/>    
        </div>
        <div style={{paddingBottom: 10}}>
            <table style={{border: 0}}>
                <thead>
                    <tr>
                        <th align='left'>Date</th>
                        <th align='left' style={{paddingLeft: 5}}>Wash Count</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td align='right'><FormLabel>Yesterday ({prop.metaData.yesterdayString})</FormLabel></td>
                        <td style={{paddingLeft: 5}}><FormControl><TextField name="iYesterday" variant='standard' value={ycount} onChange={handleChange} inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}/></FormControl></td>
                    </tr>
                    <tr>
                        <td align='right'><FormLabel>Today ({prop.metaData.todayString})</FormLabel></td>
                        <td style={{paddingLeft: 5}}><TextField name="iToday" variant='standard' value={tcount} onChange={handleChange} inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}/></td>
                    </tr>
                    <tr>
                        <td></td>
                        <td style={{paddingLeft: 5}}><Button disabled={!changed} variant="outlined" color="primary" onClick={saveChanges}>{saving ? "Saving...": "Save Changes"}</Button></td>
                    </tr>
                </tbody>
            </table>
        </div>
        </>
    )
}

const DownloadReport: React.FC<downloadProps> = (prop: downloadProps) => {

    const [loading, setLoading] = React.useState<boolean>(false);
    const [reportJson, setReportJson] = React.useState<[]>([]);
    const [csvContent, setCsvContent] = React.useState<string>('');
    const [headers, setHeaders] = React.useState<string[]>([]);

    let abort: AbortController = new AbortController();

    React.useEffect(() => {
        setReportJson([]);
        setCsvContent('');
        setHeaders([]);
        setLoading(false);

        abort.abort();
        
        
        const path = prop.link.split('/');
        const id = parseInt(path[2]);
        const uuid = path[3];
        //const { readString } = usePapaParse();
        abort = new AbortController();

        setLoading(true);
        GetDownloadReport(prop.report, prop.period, id, uuid, abort).then((report: DownloadableReport | null) => {

            if (report) {
                if (report.asJson !== '') {
                    const jsonParsed = JSON.parse(report.asJson);
                    setReportJson(jsonParsed);

                    let headers: string[] = Array.from(
                        jsonParsed.reduce((acc: any, item: any) => {
                          Object.keys(item).forEach(key => acc.add(key));
                          return acc;
                        }, new Set<string>())
                      );
                    setHeaders(headers);

                } else {
                    setHeaders([]);
                    setReportJson([]);
                }

                setCsvContent(report.asCSV);
            
            }
        }).finally(() => { setLoading(false); });

    }, [prop.period]);

    const downloadCSV = () => {
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${prop.report}-${prop.period}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            tableContainer: {
                maxHeight: 300,
                overflowY: 'auto',
                overflowX: 'auto',
                position: 'relative'
            },
            spinner: {
                marginLeft: 5,
                color: 'primary',
            },
            table: {
                width: '80%',
                borderCollapse: 'collapse',
            },
            thead: {
                position: 'sticky',
                top: 0,
                backgroundColor: '#f9f9f9',
                zIndex: 2,
                padding: 8,
                border: '1px solid #ddd'
            },
            th: {
                position: 'sticky',
                top: 0,
                backgroundColor: '#f9f9f9',
                zIndex: 2,
                padding: 8,
                border: '1px solid #ddd'
            },
            tbody: {
                border: '1px solid #ddd',
                padding: 8
            },
            td: {
                border: '1px solid #ddd',
                padding: 8
            }
        }),
    );
    const classes = useStyles();


    return (
        <>
        <Typography variant="h6">
        { loading ? <CircularProgress className={classes.spinner} size={20} /> : <></> }
        </Typography>
        { reportJson && !loading
            ? (
                <div>
                    <div style={{width: "80%", textAlign: 'left', marginBottom: 5, marginTop: 5}}>
                        <Button onClick={(e) => { downloadCSV() }} variant="outlined" color="primary">Download</Button>
                    </div>
                    <div className={classes.tableContainer}>
                        
                        <table className={classes.table}>
                            <thead>
                            <tr>
                                {headers.map((header, index) => (
                                (header != "Plan ID") ? <th className={classes.th} key={index}>{header}</th> : <></>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {reportJson.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                {headers.map((header, colIndex) => (
                                    (header != "Plan ID") ? <td className={classes.td} key={colIndex}>{row[header]}</td> : <></>
                                ))}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) 
            : loading ? 
                <Typography variant="body1">Getting your data</Typography>
                : <Typography variant="body1">No data available</Typography>
        }
        </>
    );

}

const CustomTabPanel: React.FC<TabPanelProps> = (props: TabPanelProps) => {

    return (
        <div
          role="tabpanel"
          hidden={props.value !== props.index}
          id={`simple-tabpanel-${props.index}`}
          aria-labelledby={`simple-tab-${props.index}`}
        >
          {props.value === props.index && <Box>{props.children}</Box>}
        </div>
      );

}

const LinkBack: React.FC<linkBackProps> = (prop: linkBackProps) => {

    const { user } = useContext(AuthContext);
    const hasFetched = React.useRef(false);
    const [content, setContent] = React.useState<string>('');
    const [show, setShow] = React.useState<boolean>(false);
    const [period, setPeriod] = React.useState<string>('yesterday');
    const [metaData, setMetaData] = React.useState<CMSMetaData | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [tab, setTab] = React.useState<number>(0);

    function a11yProps(index: number) {
        return {
          id: `simple-tab-${index}`,
          'aria-controls': `simple-tabpanel-${index}`,
        };
      }
    
    const handleTabChange = (e: React.ChangeEvent<{}>, newValue: any) => {
        setTab(newValue);
    }

    React.useEffect(() => {

        setLoading(true);
        if (hasFetched.current) return;
        hasFetched.current = true;

        //split path https://insights.everwash.com/engage/1/1234
        const path = prop.link.split('/');
        const id = parseInt(path[2]);
        const uuid = path[3];

        //fetch the link back
        const fetchLink = async () => {
            const link = await GetLinkBack(id, uuid);
            setContent(link);
        }
        const fetchMetaData = async (): Promise<CMSMetaData | null> => {
            const metaData = await GetMetaData(id, uuid);
            setLoading(false);
            setMetaData(metaData);
            return metaData;
        }
        
        fetchMetaData().then((md) => {
            if (md && !md.expired) {
                fetchLink();
            }
        });

    }, []);


    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
        root: {
            width: '100%',
            marginLeft: 20,
            marginTop: 10
            },
        H1: {
            fontSize: -3
        },
        heading: {
            fontSize: theme.typography.pxToRem(15),
            fontWeight: theme.typography.fontWeightRegular as number,
            },
        spinner: {
            marginLeft: 5,
            color: 'primary',
        },
        lbl: {
            color: theme.palette.primary.main,
            marginRight: theme.spacing(1)
        },
        lblRight: {
            color: theme.palette.primary.main,
            marginRight: theme.spacing(1),
            marginLeft: theme.spacing(1)
        },
        formControl: {
            margin: theme.spacing(1),
            minWidth: 120,
        },
        selectEmpty: {
            marginTop: theme.spacing(2)
        },
        header: {
            marginBottom: theme.spacing(1),
            marginTop: theme.spacing(1)
        }
        }),
    );
    const classes = useStyles();

    const handleStateChange = (metaData: CMSMetaData) => {
        setMetaData(metaData);
    }
    
    return (
        <div className={classes.root}>
        <Grid container direction="column" justify="flex-start" alignItems="stretch">
            {(metaData) ? (

                <Grid item>
                <Typography variant="h4">{metaData?.locationName}</Typography>
                {metaData ? 
                    <>
                        <Typography variant="body1"><FormLabel>Today's Date:</FormLabel> {metaData?.todayString}</Typography> 
                        {/* <Typography variant="body1"><FormLabel>Report Date:</FormLabel> {metaData?.reportDate}</Typography> */}
                    </> 
                    : <></>
                }
                </Grid>
            ) : <></> }
            {(user && user.displayName) ? (
            <Grid item><div><FormLabel>Welcome {user.displayName}</FormLabel></div></Grid>
            ) : (
            <></>
            )
            }
            <Divider />
            { (!metaData || metaData.expired) ? loading ? <Typography>Loading</Typography> : <Typography>This Link is Expired</Typography>
            : (<>
                <Survey metaData={metaData} onChange={handleStateChange} />
                <Divider />
                <div style={{paddingTop: 10}}>
                    <Typography variant="h6">Download Reports</Typography><br/>    
                </div>
                <Grid item>
                <FormControl component="fieldset">
                <ButtonGroup variant="outlined" aria-label="Basic button group">
                    <Button style={period=='yesterday' ? {backgroundColor: 'gray', color: 'white'}: {}} onClick={(e) => { setPeriod('yesterday')}}>Yesterday ({metaData?.yesterdayString})</Button>
                    <Button style={period=='today' ? {backgroundColor: 'gray', color: 'white'}: {}} onClick={(e) => { setPeriod('today')}}>Today ({metaData?.todayString})</Button>
                    <Button style={period=='lastweek' ? {backgroundColor: 'gray', color: 'white'}: {}} onClick={(e) => { setPeriod('lastweek')}}>Last 10 Days</Button>
                </ButtonGroup>
                {/* <RadioGroup row value={period} onChange={(e) => {setPeriod(e.target.value)}}>
                    <FormControlLabel value="yesterday" control={<Radio />} label={"Yesterday (" + metaData?.yesterdayString + ")"} />
                    <FormControlLabel value="today" control={<Radio />} label={"Today (" + metaData?.todayString + ")"} />
                    <FormControlLabel value="lastweek" control={<Radio />} label="Last 7 Days" />
                </RadioGroup> */}
                </FormControl>
            </Grid>
            <Grid item style={{marginTop:15}}>
            <Box>
                <Box>
                    <Tabs value={tab} onChange={handleTabChange} aria-label="basic tabs example">
                    <Tab label="Usage" {...a11yProps(0)} />
                    <Tab label="Attendant Sales" {...a11yProps(1)} />
                    <Tab label="Signups" {...a11yProps(2)} />
                    <Tab label="Attrition" {...a11yProps(3)} />
                    </Tabs>
                </Box>
                    <CustomTabPanel value={tab} index={0}>
                        <DownloadReport report="usage" title="Usage" link={prop.link} period={period} />
                    </CustomTabPanel>
                    <CustomTabPanel value={tab} index={1}>
                        <DownloadReport report="attendant" title="Attendant Sales" link={prop.link} period={period} />
                    </CustomTabPanel>
                    <CustomTabPanel value={tab} index={2}>
                        <DownloadReport report="signups" title="Signups" link={prop.link} period={period} />
                    </CustomTabPanel>
                    <CustomTabPanel value={tab} index={3}>
                        <DownloadReport report="attrition" title="Attrition" link={prop.link} period={period} />
                    </CustomTabPanel>
                </Box>
            </Grid>
            {/* <Grid item>
                <DownloadReport report="usage" title="Usage" link={prop.link} period={period} />
            </Grid>
            <Grid item style={{marginTop: 15}}>
                <DownloadReport report="attendant" title="Attendant Sales" link={prop.link} period={period} />
            </Grid>
            <Grid item style={{marginTop: 15}}>
                <DownloadReport report="signups" title="Signups" link={prop.link} period={period} />
            </Grid> */}
            {/* <Grid item style={{marginTop: 15}}>
                <Grid container direction="column" justify="flex-start" alignItems="flex-start" spacing={1}>
                    <Grid item>
                        <Typography variant="body1" className={classes.header}></Typography>
                    </Grid>
                    { (!show) ? 
                    <Grid  item>
                        <Button onClick={(e) => { setShow(true) }} variant="outlined" color="primary">Show Report</Button>
                    </Grid>
                    : <></>
                    }
                </Grid>
            </Grid> */}
            </>)
            }
            {/* { show ? (
            <>  
            <Grid item>
                { (content === '') ? <div>Loading...</div> : (
                    <div dangerouslySetInnerHTML={{__html: content}}></div>
                )}
            </Grid>
            </>
            ) : <></>
            } */}

        </Grid>
        </div>
    )
}

export default LinkBack;